import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {MatomoTracker} from 'ngx-matomo-client';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {Observable, Subject, take} from 'rxjs';
import {distinctUntilChanged, filter, map, takeUntil} from 'rxjs/operators';
import {IntentActionService} from './services/intent-action.service';
import {PortalMessageBroker} from './portal/portal-message-broker';
import {ContextInfoService, WellKnownJustFarmingProperties, WellKnownShellProperties} from './portal/context-info.service';
import {MatomoTrackerService} from './services/matomo-tracker.service';
import {Store} from '@ngrx/store';
import {AppState} from './store/states/app.state';
import {Router} from '@angular/router';
import {Actions} from '@ngrx/effects';
import {RouterSelectors, Theme, ThemeService} from '@adnova/jf-ng-components';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent  implements OnInit {
  private _intentActionService = inject(IntentActionService);
  private _portalMessageBroker = inject(PortalMessageBroker);
  private _oidcSecurityService = inject(OidcSecurityService);
  private _contextInfoService = inject(ContextInfoService);
  private readonly _tracker = inject(MatomoTracker);

  private readonly matomoTrackerService = inject(MatomoTrackerService);
  protected readonly trackMatomoEvent = this.matomoTrackerService.trackMatomoEvent;

  private readonly _unsubscribe$ = new Subject<void>();
  protected runningInPortal$: Observable<boolean>;
  private _actions$ = inject(Actions);
  private _store = inject(Store<AppState>);
  private _themeService = inject(ThemeService);
  private _destroyRef = inject(DestroyRef);
  private readonly _router = inject(Router);

  protected showImpressumButton = true;

  protected showThirdPartyContentDisclaimer = true;

  private readonly _destroyed$ = new Subject<void>();


  constructor(
    private readonly matomoRouteTrackerService: MatomoTrackerService,
  ) {
    this.runningInPortal$ = this._portalMessageBroker.runningInPortal$.pipe(takeUntil(this._unsubscribe$));
    this._contextInfoService.contextInfo$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(contextInfo => {
        let analytics = contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS);
        if (analytics === true) {
          let visitorId = contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS_VID)?.toString();
          if (visitorId) {
            this._tracker.setVisitorId(visitorId);
          }
          this._tracker.setConsentGiven();
        } else {
          this._tracker.forgetConsentGiven();
        }
      });

    this._contextInfoService.contextInfo$
      .pipe(
        map(info => info.properties.get(WellKnownShellProperties.SHELL_MODE) as string | undefined),
        distinctUntilChanged(),
        takeUntil(this._unsubscribe$),
      )
      .subscribe(mode => {
        if (mode && Object.values<string>(Theme).includes(mode)) {
          this._themeService.setTheme(mode as Theme);
        }
      });
  }


  ngOnInit(): void {
    this.listenToCurrentUrl()
  }

  doLogout(): void {
    this._oidcSecurityService.logoff().pipe(take(1)).subscribe();
  }

  private listenToCurrentUrl() {
    this._store.select(RouterSelectors.url).pipe(
      takeUntil(this._destroyed$),
      filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
    ).subscribe(url => {
      this.showImpressumButton = !url.includes('/legal/impressum');
      this.showThirdPartyContentDisclaimer = !url.includes('/legal/disclaimer');
    });
  }

}
